import { LibCategories, waitForConsent } from "src/utils/consent";
import { trackTask } from "src/utils/monitoring/events/tasks";
import { registerHandler } from "../handler";
import {
  getNoomSessionStorage,
  setNoomSessionStorage,
} from "src/utils/noomSessionStorage";
import { SessionStorageKey } from "src/utils/constants";
import getStore from "src/utils/redux/store";
import { getSurveyAnswers } from "src/hooks/survey/answers";
import { getCountryCode } from "src/utils/meristemContext";
import { appConfig } from "src/config";

const STAGING_URL = "https://staging-pt.ispot.tv/v2/TC-4722-2.gif?app=web";
const PRODUCTION_URL = "https://pt.ispot.tv/v2/TC-4722-2.gif?app=web";

// Use environment-appropriate URL
const isProduction = appConfig.NOOM_ENV === "production";
const iSpotUrl = isProduction ? PRODUCTION_URL : STAGING_URL;

// Essential fetch options for tracking pixel requests
const TRACKING_PIXEL_OPTIONS = {
  method: "GET",
  mode: "no-cors",
  credentials: "omit",
} as const;

export const buildEventParams = (
  userId,
  eventType,
  estimatedValue = 0,
  productType = "Weight"
) => {
  // Bucket value into ranges
  const getValueBucket = (value) => {
    if (value < 100) return "value_0-100";
    if (value < 200) return "value_100-200";
    if (value < 300) return "value_200-300";
    return "value_300plus";
  };

  const { ageRange, gender } = getSurveyAnswers();
  const customData = [`country_${getCountryCode()}`];

  if (ageRange?.[0]) {
    customData.push(`age_${ageRange[0]}`);
  }

  if (gender?.[0]) {
    customData.push(`gender_${gender[0].toLowerCase()}`);
  }

  if (eventType === "Signup") {
    customData.push(getValueBucket(estimatedValue));
    customData.push(`product_${productType.toLowerCase()}`);
  }

  return `&cid=${userId}&type=${eventType}&customData=${customData.join(",")}`;
};

export default function registerHandlers() {
  registerHandler("init", () => {
    waitForConsent([LibCategories.targetingCookies]).then(() => {
      if (!getNoomSessionStorage(SessionStorageKey.trackedISpotVisit)) {
        setNoomSessionStorage(SessionStorageKey.trackedISpotVisit, true);
        const { userData } = getStore().getState();
        trackTask("iSpotLead")
          .wrap(() =>
            fetch(
              `${iSpotUrl}${buildEventParams(userData.user_id, "Visit")}`,
              TRACKING_PIXEL_OPTIONS
            )
          )
          .catch(() => {
            /* NOP */
          });
      }
    });
  });

  registerHandler("lead", ({ userId }) => {
    waitForConsent([LibCategories.targetingCookies]).then(() => {
      trackTask("iSpotLead")
        .wrap(() =>
          fetch(
            `${iSpotUrl}${buildEventParams(userId, "Email_Registration")}`,
            TRACKING_PIXEL_OPTIONS
          )
        )
        .catch(() => {
          /* NOP */
        });
    });
  });

  registerHandler("startMainSurvey", () => {
    waitForConsent([LibCategories.targetingCookies]).then(() => {
      const { userData } = getStore().getState();
      trackTask("iSpotPurchase")
        .wrap(() =>
          fetch(
            `${iSpotUrl}${buildEventParams(userData.user_id, "Buyflow_Start")}`,
            TRACKING_PIXEL_OPTIONS
          )
        )
        .catch(() => {
          /* NOP */
        });
    });
  });

  registerHandler("purchase", ({ order, userId }) => {
    waitForConsent([LibCategories.targetingCookies]).then(() => {
      trackTask("iSpotPurchase")
        .wrap(() =>
          fetch(
            `${iSpotUrl}${buildEventParams(
              userId,
              "Signup",
              order.eltv_13_months
            )}`,
            TRACKING_PIXEL_OPTIONS
          )
        )
        .catch(() => {
          /* NOP */
        });
    });
  });

  registerHandler(
    "purchaseIntent",
    ({ userId, estimatedValue, medProductType }) => {
      waitForConsent([LibCategories.targetingCookies]).then(() => {
        trackTask("iSpotPurchase")
          .wrap(() =>
            fetch(
              `${iSpotUrl}${buildEventParams(
                userId,
                "Signup",
                estimatedValue,
                medProductType
              )}`,
              TRACKING_PIXEL_OPTIONS
            )
          )
          .catch(() => {
            /* NOP */
          });
      });
    }
  );
}
